/**
 * Profile Screen - User profile display
 *
 * Shows user account information with logout functionality.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { ThemePicker } from '../../components/settings/ThemePicker';
import { LanguagePicker } from '../../components/settings/LanguagePicker';
import { PersonaCard } from '../../components/settings/PersonaCard';
import { AlertWorthinessPicker } from '../../components/settings/AlertWorthinessPicker';
import { UsageWidget } from '../../components/settings/UsageWidget';
import { getProfile } from '../../services/authApi';
import { getToken, clearAllTokens } from '../../services/tokenStorage';
import { UserProfile } from '../../services/authApi';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { SettingsNavigatorParamList } from '../../navigation/SettingsNavigator';
import { showConfirm, showError, showSuccess } from '../../utils/alert';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserSettings, useUpdateUserSettings } from '../../services/api/queries/useUserSettings';

interface CurrencyOption {
  value: string;
  label: string;
  description: string;
}

const SUPPORTED_CURRENCIES: CurrencyOption[] = [
  { value: 'USD', label: 'USD', description: 'U.S. Dollar' },
  { value: 'HKD', label: 'HKD', description: 'Hong Kong Dollar' },
  { value: 'TWD', label: 'TWD', description: 'Taiwan Dollar' },
  { value: 'JPY', label: 'JPY', description: 'Japanese Yen' },
  { value: 'GBP', label: 'GBP', description: 'British Pound' },
  { value: 'EUR', label: 'EUR', description: 'Euro' },
  { value: 'SGD', label: 'SGD', description: 'Singapore Dollar' },
  { value: 'CNY', label: 'CNY', description: 'Chinese Yuan' },
];

interface SelectOption {
  label: string;
  value: string;
}

interface SelectSettingSectionProps {
  label: string;
  description: string;
  selectedValue: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  disabled: boolean;
  saving: boolean;
  savingText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: any;
}

/**
 * A labelled dropdown settings row (Base Currency, Answer Style). The web-only
 * <select> theming rule is global, so it lives in ProfileScreen and is injected
 * once — not repeated per section.
 */
function SelectSettingSection({
  label,
  description,
  selectedValue,
  onValueChange,
  options,
  disabled,
  saving,
  savingText,
  theme,
  styles,
}: SelectSettingSectionProps) {
  return (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionLabel}>{label}</Text>
      <Text style={styles.sectionDescription}>{description}</Text>
      <View
        style={[
          styles.currencyPickerWrapper,
          {
            borderColor: theme.colors.border,
            borderWidth: theme.id === 'bauhaus' ? 2 : 1,
          },
        ]}
      >
        <Picker
          selectedValue={selectedValue}
          onValueChange={onValueChange}
          enabled={!disabled}
          style={[
            styles.currencyPicker,
            { color: theme.colors.text, backgroundColor: theme.colors.backgroundElevated },
          ]}
          dropdownIconColor={theme.colors.textSecondary}
          mode="dropdown"
        >
          {options.map((option) => (
            <Picker.Item
              key={option.value}
              label={option.label}
              value={option.value}
              color={theme.colors.text}
            />
          ))}
        </Picker>
      </View>
      {saving && (
        <View style={styles.savingIndicator}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={styles.savingText}>{savingText}</Text>
        </View>
      )}
    </View>
  );
}

export function ProfileScreen() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Which settings field is mid-save (null = idle). Tracked per field so only the
  // section the user changed shows its saving indicator, while every picker is
  // disabled for the duration.
  const [savingField, setSavingField] = useState<string | null>(null);
  const navigation =
    useNavigation<StackNavigationProp<SettingsNavigatorParamList, 'UserProfile'>>();
  const { setIsAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { data: userSettings, isLoading: settingsLoading } = useUserSettings();
  const updateUserSettings = useUpdateUserSettings();
  const { t } = useTranslation();

  // Create themed styles
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const loadProfile = async () => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const data = await getProfile(token);
      setProfile(data);
    } catch (error) {
      showError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Reload on focus, not just on mount: popping back from ChangeEmailScreen does
  // not remount this screen, so a mount-only effect would keep showing the old
  // address after a successful change. Matches the useFocusEffect idiom already
  // used by PositionsScreen, TradesScreen and PortfolioTab.
  useFocusEffect(
    useCallback(() => {
      loadProfile();
    }, [loadProfile])
  );

  const handleLogout = async () => {
    const confirmed = await showConfirm(
      t('settings.logoutConfirmTitle'),
      t('settings.logoutConfirmMessage')
    );

    if (confirmed) {
      await clearAllTokens();
      setIsAuthenticated(false);
    }
  };

  const handleCurrencyChange = async (currency: string) => {
    if (savingField) return;

    setSavingField('base_currency');
    try {
      // mutateAsync (not mutate): mutate() returns void and never rejects in
      // React Query v5, so awaiting it would make this catch dead and surface
      // failures as unhandled rejections.
      await updateUserSettings.mutateAsync({ base_currency: currency });
      showSuccess('Settings updated', `Base currency changed to ${currency}`);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to update currency');
    } finally {
      setSavingField(null);
    }
  };

  const handleAnswerStyleChange = async (style: string) => {
    if (savingField) return;

    setSavingField('answer_style');
    try {
      await updateUserSettings.mutateAsync({ answer_style: style });
      showSuccess('Settings updated', `Answer style changed to ${style}`);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to update answer style');
    } finally {
      setSavingField(null);
    }
  };

  const currencyOptions = useMemo<SelectOption[]>(
    () =>
      SUPPORTED_CURRENCIES.map((c) => ({
        label: `${c.label} - ${c.description}`,
        value: c.value,
      })),
    []
  );

  const answerStyleOptions = useMemo<SelectOption[]>(
    () => [
      { label: t('settings.answerStyleConcise'), value: 'concise' },
      { label: t('settings.answerStylePro'), value: 'pro' },
    ],
    [t]
  );

  if (loading || settingsLoading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!profile) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>
          {t('errors.failedToLoad', { item: t('settings.myProfile') })}
        </Text>
        <View style={styles.buttonContainer}>
          <Button title={t('common.retry')} onPress={loadProfile} style={styles.retryButton} />
          <Button
            title={t('auth.logout')}
            onPress={handleLogout}
            variant="danger"
            style={styles.logoutButton}
          />
        </View>
      </View>
    );
  }

  return (
    <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>{t('settings.myProfile')}</Text>
      </View>

      <View style={styles.infoContainer}>
        <View style={styles.infoRow}>
          <Text style={styles.label}>{t('settings.usernameLabel')}:</Text>
          <Text style={styles.value}>{profile.username}</Text>
        </View>

        <TouchableOpacity
          style={styles.infoRow}
          onPress={() => navigation.navigate('ChangeEmail')}
          activeOpacity={0.7}
        >
          <Text style={styles.label}>{t('settings.emailLabel')}:</Text>
          <Text style={styles.value}>{profile.email}</Text>
          <Text style={styles.changeHint}>{t('settings.changeEmail')}</Text>
        </TouchableOpacity>

        <View style={styles.infoRow}>
          <Text style={styles.label}>{t('settings.memberSince')}:</Text>
          <Text style={styles.value}>{new Date(profile.created_at).toLocaleDateString()}</Text>
        </View>
      </View>

      {/* Language Section */}
      <LanguagePicker />

      {/* Web-only dark-mode theming for every <select> on the page. It is a
          global rule, so it is injected once here rather than per section. */}
      {Platform.OS === 'web' && (
        <style>{`
          select {
            background-color: ${theme.colors.backgroundElevated} !important;
            color: ${theme.colors.text} !important;
          }
          select option {
            background-color: ${theme.colors.backgroundElevated};
            color: ${theme.colors.text};
          }
        `}</style>
      )}

      {/* Base Currency Section */}
      <SelectSettingSection
        label={t('settings.baseCurrency')}
        description={t('settings.baseCurrencyDescription')}
        selectedValue={userSettings?.base_currency || 'USD'}
        onValueChange={handleCurrencyChange}
        options={currencyOptions}
        disabled={!!savingField}
        saving={savingField === 'base_currency'}
        savingText={t('common.saving')}
        theme={theme}
        styles={styles}
      />

      {/* Answer Style Section */}
      <SelectSettingSection
        label={t('settings.answerStyle')}
        description={t('settings.answerStyleDescription')}
        selectedValue={userSettings?.answer_style || 'concise'}
        onValueChange={handleAnswerStyleChange}
        options={answerStyleOptions}
        disabled={!!savingField}
        saving={savingField === 'answer_style'}
        savingText={t('common.saving')}
        theme={theme}
        styles={styles}
      />

      {/* Investor persona (revamp T25): knowledge level picker + read-only
          style/risk summary. Sits beside the answer-style row it complements —
          one tunes depth, the other tunes register. */}
      <PersonaCard />

      {/* Alert worthiness (revamp T26): which briefing alerts (stop-loss /
          target / catalyst) reach the user's action items. The settings-side
          notification surface — push delivery itself stays token-based. */}
      <AlertWorthinessPicker />

      <UsageWidget />

      <ThemePicker />

      <Button
        title={t('settings.logoutButton')}
        onPress={handleLogout}
        variant="danger"
        style={styles.logoutButton}
      />
    </ScrollView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    scrollContainer: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    scrollContent: {
      padding: 20,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    headerTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    infoContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: 16,
      marginBottom: 24,
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    sectionContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: 16,
      marginBottom: 24,
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    sectionLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 4,
    },
    sectionDescription: {
      fontSize: 14,
      color: theme.colors.textSecondary,
      marginBottom: 12,
    },
    currencyPickerWrapper: {
      borderRadius: theme.radii.md,
      overflow: 'hidden',
      backgroundColor: theme.colors.backgroundElevated,
    },
    currencyPicker: {
      height: Platform.OS === 'web' ? 44 : undefined,
      fontSize: theme.typography.fontSizeMd,
      backgroundColor: 'transparent',
    },
    savingIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 8,
    },
    savingText: {
      marginLeft: 8,
      fontSize: 14,
      color: theme.colors.textSecondary,
    },
    infoRow: {
      marginBottom: 16,
    },
    label: {
      fontSize: 14,
      color: theme.colors.textSecondary,
      marginBottom: 4,
    },
    value: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: '500',
    },
    changeHint: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.primary,
      fontWeight: '600',
    },
    logoutButton: {
      marginTop: 24,
    },
    errorText: {
      fontSize: 16,
      color: theme.colors.error,
      textAlign: 'center',
      marginBottom: 16,
    },
    buttonContainer: {
      marginTop: 16,
      gap: 12,
    },
    retryButton: {
      marginBottom: 8,
    },
  });
}
